<div class="org-settings-card">
  <div class="card-header">
    <h3 class="section-title">Organisationsindstillinger</h3>
    <p class="section-desc">
      Indstillinger der gælder for denne organisation. Åbn en gruppe for at
      finde den indstilling, du leder efter.
    </p>
  </div>

  @if (errorMessage) {
  <p class="error-message">{{ errorMessage }}</p>
  } @if (loading) {
  <p class="loading-message">Henter indstillinger...</p>
  } @if (!loading && effectiveOrgId != null && settingsLoaded) {
  <mat-accordion class="settings-accordion" multi>
    <mat-expansion-panel [expanded]="true">
      <mat-expansion-panel-header>
        <mat-panel-title>Grundlæggende</mat-panel-title>
        <mat-panel-description>Organisationens navn</mat-panel-description>
      </mat-expansion-panel-header>
      <section class="settings-section settings-section--panel">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Organisationens navn</mat-label>
          <input matInput [(ngModel)]="data['name']" placeholder="Fulde navn" />
        </mat-form-field>
      </section>
    </mat-expansion-panel>
    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title>Øvelser og events</mat-panel-title>
        <mat-panel-description
          >Tilmelding, redigering, venteliste og kalender</mat-panel-description
        >
      </mat-expansion-panel-header>
      <section class="settings-section settings-section--panel">
        <div class="checkbox-list">
          @for (def of checkboxDefs; track def.key) {
          <div class="checkbox-item">
            <mat-slide-toggle
              [checked]="getCheckboxValue(def.key)"
              (change)="setCheckboxValue(def.key, $event.checked)"
            >
              {{ def.label }}
            </mat-slide-toggle>
            <p class="checkbox-description">{{ def.description }}</p>
          </div>
          }
        </div>
      </section>
    </mat-expansion-panel>
    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title>Brugere og grupper</mat-panel-title>
        <mat-panel-description
          >Brugeradministration, grupperinger og
          kundearkiv</mat-panel-description
        >
      </mat-expansion-panel-header>
      <section class="settings-section settings-section--panel">
        <h5 class="panel-subgroup-title">Brugeradministration</h5>
        <div class="checkbox-list">
          @for (def of userAdminCheckboxDefs; track def.key) {
          <div class="checkbox-item">
            <mat-slide-toggle
              [checked]="getCheckboxValue(def.key)"
              (change)="setCheckboxValue(def.key, $event.checked)"
            >
              {{ def.label }}
            </mat-slide-toggle>
            <p class="checkbox-description">{{ def.description }}</p>
          </div>
          }
        </div>
        <h5 class="panel-subgroup-title">Brugergrupperinger</h5>
        <div class="checkbox-list">
          @for (def of userGroupsCheckboxDefs; track def.key) {
          <div class="checkbox-item">
            <mat-slide-toggle
              [checked]="getCheckboxValue(def.key)"
              (change)="
                def.key === 'group_calendar_enabled'
                  ? onGroupCalendarEnabledChange($event.checked)
                  : setCheckboxValue(def.key, $event.checked)
              "
            >
              {{ def.label }}
            </mat-slide-toggle>
            <p class="checkbox-description">{{ def.description }}</p>
          </div>
          }
        </div>
        @if (getCheckboxValue('group_calendar_enabled')) {
        <h5 class="panel-subgroup-title">Bruger-gruppekalender</h5>
        <p class="section-hint">
          Indstillinger for den bruger-vendte gruppekalender. Disse gælder ikke
          for admin-kalenderen.
        </p>
        <div class="checkbox-list">
          <div class="checkbox-item">
            <mat-slide-toggle
              [checked]="
                getCheckboxValue('group_calendar_user_show_all_locations')
              "
              (change)="
                setCheckboxValue(
                  'group_calendar_user_show_all_locations',
                  $event.checked
                )
              "
            >
              Vis alle lokationer
            </mat-slide-toggle>
            <p class="checkbox-description">
              Når aktiveret, vises alle lokationer fra gruppe-kalenderens
              indstillinger — uanset brugerens egne tilknyttede lokationer.
              Deaktiver for kun at vise kolonner for brugerens egne lokationer.
            </p>
          </div>
        </div>
        <h6 class="panel-subgroup-subtitle">Roller der må se kalenderen</h6>
        <p class="section-hint">
          Vælg hvilke roller der kan se bruger-gruppekalenderen. Org-bruger og
          org-admin er altid inkluderet.
        </p>
        <div class="group-calendar-roles-list">
          @for (opt of groupCalendarRoleOptions; track opt.role) {
          <label class="group-calendar-role-option">
            <input
              type="checkbox"
              [checked]="isGroupCalendarViewRoleSelected(opt.role)"
              [disabled]="!!opt.alwaysOn"
              (change)="
                onToggleGroupCalendarViewRole(
                  opt.role,
                  $any($event.target).checked
                )
              "
            />
            {{ opt.label }}
            @if (opt.alwaysOn) {
            <span class="always-on-badge">Altid aktiv</span>
            }
          </label>
          }
        </div>
        <h6 class="panel-subgroup-subtitle">
          Uddannelsesveje med redigeringsrettighed
        </h6>
        <p class="section-hint">
          Brugere tilknyttet en af de valgte uddannelsesveje kan oprette, flytte
          og slette events i gruppekalenderen — men kun på egne lokationer.
          Org-bruger, org-admin og superadmin har altid redigeringsadgang.
        </p>
        <p class="section-hint section-hint--info">
          Vælg uddannelsesvejen <strong>Uddannelseskoordinator</strong> herunder
          for at give redigeringsrettighed til gruppeøvelseskalenderen. Tilføj
          derefter de ønskede brugere til den uddannelsesvej — de får automatisk
          adgang til at oprette, flytte og slette events på de lokationer, de er
          tilknyttet. Org-bruger, org-admin og superadmin har altid
          redigeringsadgang uanset uddannelsesvej.
        </p>
        @if (loadingGroupCalendarPaths) {
        <p class="loading-message">Henter uddannelsesveje...</p>
        } @if (!loadingGroupCalendarPaths) {
        <div>
          @if (groupCalendarEditPaths.length === 0) {
          <p class="section-hint">
            Ingen uddannelsesveje fundet. Opret uddannelsesveje under
            Uddannelsesveje.
          </p>
          } @if (groupCalendarEditPaths.length > 0) {
          <input
            class="path-search-input"
            type="search"
            placeholder="Søg uddannelsesveje…"
            [(ngModel)]="groupCalendarPathSearch"
            aria-label="Søg uddannelsesveje"
          />
          <div class="group-calendar-paths-list">
            @if (filteredGroupCalendarEditPaths.length === 0) {
            <p class="section-hint">Ingen uddannelsesveje matcher søgningen.</p>
            } @for (path of filteredGroupCalendarEditPaths; track path.path_id) {
            <label class="resource-default-option">
              <input
                type="checkbox"
                [checked]="isGroupCalendarEditPathSelected(path.path_id)"
                (change)="
                  onToggleGroupCalendarEditPath(
                    path.path_id,
                    $any($event.target).checked
                  )
                "
              />
              {{ path.name }}
            </label>
            }
          </div>
          }
        </div>
        } }
        <h5 class="panel-subgroup-title">Kundearkiv</h5>
        <div class="checkbox-list">
          @for (def of customerArchiveCheckboxDefs; track def.key) {
          <div class="checkbox-item">
            <mat-slide-toggle
              [checked]="getCheckboxValue(def.key)"
              (change)="setCheckboxValue(def.key, $event.checked)"
            >
              {{ def.label }}
            </mat-slide-toggle>
            <p class="checkbox-description">{{ def.description }}</p>
          </div>
          }
        </div>
        <h5 class="panel-subgroup-title">Forplejning ved oprettelse</h5>
        <p class="section-hint">
          Styr for hver begivenhedstype om feltet «Forplejning» vises i
          opret/rediger-event — inkl. per dag i planlæggeren. Standard er
          aktiveret for alle typer.
        </p>
        <div class="register-deadline-list">
          @for (row of eventCateringFieldTypes; track row.eventType) {
          <div class="register-deadline-item">
            <div class="register-deadline-toggle-row">
              <mat-slide-toggle
                [checked]="getCateringFieldEnabled(row.eventType)"
                (change)="setCateringFieldEnabled(row.eventType, $event.checked)"
              >
                {{ row.label }}
              </mat-slide-toggle>
            </div>
            <p class="checkbox-description register-deadline-desc">
              {{ row.description }}
            </p>
          </div>
          }
        </div>
        <h5 class="panel-subgroup-title">Moduler ved oprettelse</h5>
        <p class="section-hint">
          Styr for hver begivenhedstype om modul-side (side 2) og moduler i
          serieplanlæggeren vises ved opret/rediger-event. Standard er aktiveret
          for alle typer.
        </p>
        <div class="register-deadline-list">
          @for (row of eventModulesFieldTypes; track row.eventType) {
          <div class="register-deadline-item">
            <div class="register-deadline-toggle-row">
              <mat-slide-toggle
                [checked]="getModulesFieldEnabled(row.eventType)"
                (change)="setModulesFieldEnabled(row.eventType, $event.checked)"
              >
                {{ row.label }}
              </mat-slide-toggle>
            </div>
            <p class="checkbox-description register-deadline-desc">
              {{ row.description }}
            </p>
          </div>
          }
        </div>
      </section>
    </mat-expansion-panel>
    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title>Tilmelding og afmelding</mat-panel-title>
        <mat-panel-description>Frister før eventstart</mat-panel-description>
      </mat-expansion-panel-header>
      <section class="settings-section settings-section--panel">
        <h5 class="panel-subgroup-title">Afmelding før eventstart</h5>
        <p class="section-hint">
          Styr for hver event-type om deltagere selv kan afmelde sig, og hvor
          mange dage før eventets start afmelding lukker. 1 dag svarer til den
          tidligere standard på 24 timer før start.
        </p>
        <div class="register-deadline-list">
          @for (row of unregisterEventTypes; track row.eventType) {
          <div class="register-deadline-item">
            <div class="register-deadline-toggle-row">
              <mat-slide-toggle
                [checked]="getUnregisterEnabled(row.eventType)"
                (change)="setUnregisterEnabled(row.eventType, $event.checked)"
              >
                {{ row.label }}
              </mat-slide-toggle>
            </div>
            <p class="checkbox-description register-deadline-desc">
              {{ row.description }}
            </p>
            @if (getUnregisterEnabled(row.eventType)) {
            <mat-form-field
              appearance="outline"
              class="register-deadline-days-field"
            >
              <mat-label>Antal dage før start (afmelding lukker)</mat-label>
              <input
                matInput
                type="number"
                min="1"
                step="1"
                [ngModel]="getUnregisterDays(row.eventType)"
                (ngModelChange)="onUnregisterDaysChange(row.eventType, $event)"
              />
            </mat-form-field>
            }
          </div>
          }
        </div>
        <h5 class="panel-subgroup-title">Seneste tilmelding før eventstart</h5>
        <p class="section-hint">
          Styr for hver event-type om tilmelding via kalenderen skal stoppe et
          antal dage før eventets starttid. 0 dage betyder at tilmelding er
          mulig frem til (men ikke efter) starttidspunktet. Gælder selvbetjent
          tilmelding (kalender/deltag); administratorer kan stadig tilføje
          deltagere via deltager-administration, når det er tilladt af roller.
        </p>
        <div class="register-deadline-list">
          @for (row of registerDeadlineTypes; track row.eventType) {
          <div class="register-deadline-item">
            <div class="register-deadline-toggle-row">
              <mat-slide-toggle
                [checked]="getRegisterDeadlineEnabled(row.eventType)"
                (change)="
                  setRegisterDeadlineEnabled(row.eventType, $event.checked)
                "
              >
                {{ row.label }}
              </mat-slide-toggle>
            </div>
            <p class="checkbox-description register-deadline-desc">
              {{ row.description }}
            </p>
            @if (getRegisterDeadlineEnabled(row.eventType)) {
            <mat-form-field
              appearance="outline"
              class="register-deadline-days-field"
            >
              <mat-label>Antal dage før start (minimum)</mat-label>
              <input
                matInput
                type="number"
                min="0"
                step="1"
                [ngModel]="getRegisterDeadlineDays(row.eventType)"
                (ngModelChange)="
                  onRegisterDeadlineDaysChange(row.eventType, $event)
                "
              />
            </mat-form-field>
            }
          </div>
          }
        </div>
      </section>
    </mat-expansion-panel>
    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title>Materiel</mat-panel-title>
        <mat-panel-description
          >Event-materiel, ledighed og e-mail-flow</mat-panel-description
        >
      </mat-expansion-panel-header>
      <section class="settings-section settings-section--panel">
        <div class="checkbox-list">
          @for (def of materialsCheckboxDefs; track def.key) {
          <div class="checkbox-item">
            <mat-slide-toggle
              [checked]="getCheckboxValue(def.key)"
              (change)="setCheckboxValue(def.key, $event.checked)"
            >
              {{ def.label }}
            </mat-slide-toggle>
            <p class="checkbox-description">{{ def.description }}</p>
          </div>
          }
        </div>
        @if (getCheckboxValue('materials_enabled')) {
        <mat-form-field appearance="outline">
          <mat-label>Buffer dage før/efter event</mat-label>
          <input
            matInput
            type="number"
            min="0"
            max="30"
            [(ngModel)]="materialsBufferDays"
          />
        </mat-form-field>
        }
      </section>
    </mat-expansion-panel>
    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title>Påmindelser</mat-panel-title>
        <mat-panel-description>E-mail før eventstart</mat-panel-description>
      </mat-expansion-panel-header>
      <section class="settings-section settings-section--panel">
        <p class="section-hint">
          Standard er én primær påmindelse 1 dag før start for alle tre typer.
          Påmindelsen køes ved tilmelding og sendes af notifikations-køen når
          tidspunktet nås. Du kan tilføje en valgfri ekstra påmindelse på et
          andet tidspunkt (mindst 1 dag før start).
        </p>
        <div class="register-deadline-list">
          @for (row of eventReminderNotifyTypes; track row.eventType) {
          <div class="register-deadline-item">
            <div class="register-deadline-toggle-row">
              <mat-slide-toggle
                [checked]="getEventReminderNotifyEnabled(row.eventType)"
                (change)="
                  setEventReminderNotifyEnabled(row.eventType, $event.checked)
                "
              >
                {{ row.label }} – primær påmindelse
              </mat-slide-toggle>
            </div>
            <p class="checkbox-description register-deadline-desc">
              {{ row.description }}
            </p>
            @if (getEventReminderNotifyEnabled(row.eventType)) {
            <mat-form-field
              appearance="outline"
              class="register-deadline-days-field"
            >
              <mat-label>Primær: antal dage før start</mat-label>
              <input
                matInput
                type="number"
                min="1"
                step="1"
                [ngModel]="getEventReminderNotifyDays(row.eventType)"
                (ngModelChange)="
                  onEventReminderNotifyDaysChange(row.eventType, $event)
                "
              />
            </mat-form-field>
            }
            <div class="reminder-extra-block">
              <div class="register-deadline-toggle-row">
                <mat-slide-toggle
                  [checked]="getEventReminderExtraEnabled(row.eventType)"
                  (change)="
                    setEventReminderExtraEnabled(row.eventType, $event.checked)
                  "
                >
                  Ekstra påmindelse
                </mat-slide-toggle>
              </div>
              @if (getEventReminderExtraEnabled(row.eventType)) {
              <mat-form-field
                appearance="outline"
                class="register-deadline-days-field"
              >
                <mat-label>Ekstra: antal dage før start</mat-label>
                <input
                  matInput
                  type="number"
                  min="1"
                  step="1"
                  [ngModel]="getEventReminderExtraDays(row.eventType)"
                  (ngModelChange)="
                    onEventReminderExtraDaysChange(row.eventType, $event)
                  "
                />
              </mat-form-field>
              }
            </div>
          </div>
          }
        </div>
      </section>
    </mat-expansion-panel>
    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title>Standard notifikationer</mat-panel-title>
        <mat-panel-description
          >Mail og push for nye brugere</mat-panel-description
        >
      </mat-expansion-panel-header>
      <section class="settings-section settings-section--panel">
        <p class="section-hint">
          Standard for event-notifikationer for brugere der ikke har tilpasset
          egne indstillinger. Brugere der gemmer egne valg i profil/indstillinger
          overskriver altid disse standarder.
        </p>
        <div class="notification-pref-table">
          <div class="notification-pref-header">
            <span>Notifikation</span>
            <span>E-mail</span>
            <span>Push</span>
          </div>
          @for (row of notificationPreferenceRows; track row.key) {
          <div class="notification-pref-row">
            <span>{{ row.label }}</span>
            <mat-slide-toggle
              [checked]="getNotificationPrefEmail(row.key)"
              (change)="setNotificationPrefEmail(row.key, $event.checked)"
            ></mat-slide-toggle>
            <mat-slide-toggle
              [checked]="getNotificationPrefPush(row.key)"
              (change)="setNotificationPrefPush(row.key, $event.checked)"
            ></mat-slide-toggle>
          </div>
          }
        </div>
      </section>
    </mat-expansion-panel>
    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title>Kalender og visning</mat-panel-title>
        <mat-panel-description
          >Event-kategorier og farver</mat-panel-description
        >
      </mat-expansion-panel-header>
      <section class="settings-section settings-section--panel">
        <p class="section-hint">
          Angiv kategorier og tilhørende farver, så events kan kategoriseres og
          kalenderen kan vise farven.
        </p>
        @for (item of categoryColors; track trackByIndex($index); let i =
        $index) {
        <div class="category-colors category-row">
          <mat-form-field appearance="outline" class="category-name">
            <mat-label>Kategori</mat-label>
            <input
              matInput
              [(ngModel)]="item.category"
              placeholder="f.eks. Kursus"
            />
          </mat-form-field>
          <div class="color-input-wrap">
            <label class="color-label">Farve</label>
            <input type="color" [(ngModel)]="item.color" class="color-picker" />
            <input
              type="text"
              [(ngModel)]="item.color"
              class="color-hex"
              placeholder="#1976d2"
              maxlength="7"
            />
          </div>
          <button
            mat-icon-button
            type="button"
            (click)="removeCategoryColor(i)"
            aria-label="Fjern"
          >
            <span class="remove-btn">&times;</span>
          </button>
        </div>
        }
        <button mat-stroked-button type="button" (click)="addCategoryColor()">
          + Tilføj kategori
        </button>
      </section>
    </mat-expansion-panel>
    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title>Ressourcer</mat-panel-title>
        <mat-panel-description
          >Ekstra lokaler, bookbare ressourcer, prisgrupper og standardvalg for
          ressourceoversigt</mat-panel-description
        >
      </mat-expansion-panel-header>
      <section class="settings-section settings-section--panel">
        <h5 class="panel-subgroup-title">Ekstra lokaler på events</h5>
        <p class="section-hint">
          Når aktiveret, kan org-brugere tilføje flere lokaler fra samme
          lokation per dag/session ved oprettelse og redigering af events.
        </p>
        <div class="checkbox-list">
          <mat-slide-toggle
            [checked]="getEventsAdditionalRoomsEnabled()"
            (change)="setEventsAdditionalRoomsEnabled($event.checked)"
          >
            Tillad ekstra lokaler på events
          </mat-slide-toggle>
        </div>
        <h5 class="panel-subgroup-title">Bookbare ressourcer</h5>
        <p class="section-hint">
          Definér ressourcer (fx projektor, kaffevogn), som kan tilknyttes
          events og vises i ressourceoversigten.
        </p>
        @if (loadingOrgResources) {
        <p class="loading-message">Henter bookbare ressourcer…</p>
        } @if (orgResourcesError) {
        <p class="error-message">{{ orgResourcesError }}</p>
        } @if (!loadingOrgResources) {
        <div class="price-groups-block">
          <div class="price-group-create-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Ny ressource</mat-label>
              <input
                matInput
                [(ngModel)]="newOrgResourceLabel"
                (keyup.enter)="createOrgBookableResource()"
                placeholder="Fx Projektor, Kaffevogn"
                [disabled]="creatingOrgResource"
              />
            </mat-form-field>
            <button
              mat-raised-button
              color="primary"
              type="button"
              (click)="createOrgBookableResource()"
              [disabled]="creatingOrgResource"
            >
              {{ creatingOrgResource ? "Opretter…" : "Tilføj" }}
            </button>
          </div>
          @if (orgBookableResources.length > 0) {
          <div class="price-group-list">
            @for (resource of orgBookableResources; track resource.id) {
            <div class="price-group-row">
              <mat-form-field
                appearance="outline"
                class="price-group-label-field"
              >
                <mat-label>Navn</mat-label>
                <input
                  matInput
                  [(ngModel)]="orgResourceLabelDraft[resource.id]"
                  [disabled]="savingOrgResourceId === resource.id"
                />
              </mat-form-field>
              <mat-slide-toggle
                [checked]="resource.is_active"
                [disabled]="togglingOrgResourceId === resource.id"
                (change)="onOrgResourceActiveChange(resource, $event)"
              >
                Aktiv
              </mat-slide-toggle>
              <button
                mat-stroked-button
                color="primary"
                type="button"
                (click)="saveOrgResourceLabel(resource)"
                [disabled]="
                  savingOrgResourceId === resource.id ||
                  !orgResourceLabelDirty(resource)
                "
              >
                Gem
              </button>
              <button
                mat-stroked-button
                color="warn"
                type="button"
                (click)="deleteOrgBookableResource(resource)"
                [disabled]="deletingOrgResourceId === resource.id"
              >
                Slet
              </button>
            </div>
            }
          </div>
          } @else {
          <p class="section-hint">Ingen bookbare ressourcer endnu.</p>
          }
        </div>
        }
        <h5 class="panel-subgroup-title">Prisgrupper</h5>
        <p class="section-hint">
          Definér prisgrupper til kursusprisskabeloner og kundetilknytning.
          Deaktiverede grupper vises ikke i nye events.
        </p>
        @if (loadingPriceGroups) {
        <p class="loading-message">Henter prisgrupper…</p>
        } @if (priceGroupsError) {
        <p class="error-message">{{ priceGroupsError }}</p>
        } @if (!loadingPriceGroups) {
        <div class="price-groups-block">
          <div class="price-group-create-row">
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Ny prisgruppe</mat-label>
              <input
                matInput
                [(ngModel)]="newPriceGroupLabel"
                (keyup.enter)="createPriceGroup()"
                placeholder="Fx Standard, Partner A"
                [disabled]="creatingPriceGroup"
              />
            </mat-form-field>
            <button
              mat-raised-button
              color="primary"
              type="button"
              (click)="createPriceGroup()"
              [disabled]="creatingPriceGroup"
            >
              {{ creatingPriceGroup ? "Opretter…" : "Tilføj" }}
            </button>
          </div>
          @if (priceGroups.length > 0) {
          <div class="price-group-list">
            @for (group of priceGroups; track group.id) {
            <div class="price-group-row">
              <mat-form-field
                appearance="outline"
                class="price-group-label-field"
              >
                <mat-label>Navn</mat-label>
                <input
                  matInput
                  [(ngModel)]="priceGroupLabelDraft[group.id]"
                  [disabled]="savingPriceGroupId === group.id"
                />
              </mat-form-field>
              <mat-slide-toggle
                [checked]="group.is_active"
                [disabled]="togglingPriceGroupId === group.id"
                (change)="onPriceGroupActiveChange(group, $event)"
              >
                Aktiv
              </mat-slide-toggle>
              <button
                mat-stroked-button
                color="primary"
                type="button"
                (click)="savePriceGroupLabel(group)"
                [disabled]="
                  savingPriceGroupId === group.id ||
                  !priceGroupLabelDirty(group)
                "
              >
                Gem
              </button>
              <button
                mat-stroked-button
                color="warn"
                type="button"
                (click)="deletePriceGroup(group)"
                [disabled]="deletingPriceGroupId === group.id"
              >
                Slet
              </button>
            </div>
            }
          </div>
          } @else {
          <p class="section-hint">Ingen prisgrupper endnu.</p>
          }
        </div>
        }
        <h5 class="panel-subgroup-title">Ressourceoversigt</h5>
        <p class="section-hint">
          Vælg hvilke lokationer/lokaler og eventtyper der skal være valgt som
          standard i ressourceoversigten.
        </p>
        <div class="checkbox-list resource-overview-manual-toggle">
          <div class="checkbox-item">
            <mat-slide-toggle
              [checked]="getResourceOverviewShowManualLocations()"
              (change)="setResourceOverviewShowManualLocations($event.checked)"
            >
              Vis manuelle lokationer som standard
            </mat-slide-toggle>
            <p class="checkbox-description">
              Når aktiveret, vises events med manuelt angivne lokationer som
              egne rækker i ressourceoversigten (grupperet efter navn).
              Deaktiver for at skjule dem som standard — brugere kan stadig slå
              visningen til i kalenderen.
            </p>
          </div>
          <div class="checkbox-item">
            <mat-slide-toggle
              [checked]="getResourceOverviewShowLocationRows()"
              (change)="setResourceOverviewShowLocationRows($event.checked)"
            >
              Vis lokationer som egne rækker som standard
            </mat-slide-toggle>
            <p class="checkbox-description">
              Når aktiveret, vises lokationen som en række over dens lokaler i
              ressourceoversigten. Deaktiver for kun at vise lokalerne, når
              lokationen har lokaler. Lokationer uden lokaler vises stadig.
              Brugere kan stadig ændre visningen i visningsindstillingerne.
            </p>
          </div>
        </div>
        @if (loadingResourceDefaults) {
        <p class="loading-message">Henter lokationer og lokaler...</p>
        } @if (!loadingResourceDefaults) {
        <div class="resource-defaults-grid">
          <div class="resource-defaults-column">
            <h5>Eventtyper (default)</h5>
            @for (option of resourceOverviewEventTypes; track option.value) {
            <label class="resource-default-option">
              <input
                type="checkbox"
                [checked]="isResourceDefaultEventTypeSelected(option.value)"
                (change)="
                  onToggleResourceDefaultEventType(
                    option.value,
                    $any($event.target).checked
                  )
                "
              />
              {{ option.label }}
            </label>
            }
          </div>
          <div class="resource-defaults-column">
            <h5>Bookbare ressourcer (default)</h5>
            @for (resource of orgBookableResources; track resource.id) {
            <label class="resource-default-option">
              <input
                type="checkbox"
                [checked]="isResourceDefaultOrgResourceSelected(resource.id)"
                (change)="
                  onToggleResourceDefaultOrgResource(
                    resource.id,
                    $any($event.target).checked
                  )
                "
              />
              {{ resource.label }}@if (!resource.is_active) { (inaktiv) }
            </label>
            } @if (orgBookableResources.length === 0) {
            <p class="section-hint">Ingen bookbare ressourcer oprettet.</p>
            }
          </div>
          <div class="resource-defaults-column">
            <h5>Lokationer og lokaler (default)</h5>
            @for (location of resourceDefaultLocations; track location.guid) {
            <div class="location-default-group">
              <label class="resource-default-option location">
                <input
                  type="checkbox"
                  [checked]="isResourceDefaultLocationSelected(location.guid)"
                  (change)="
                    onToggleResourceDefaultLocation(
                      location.guid,
                      $any($event.target).checked
                    )
                  "
                />
                {{ location.locations_name }}
              </label>
              @for (room of getRoomsByLocation(location.guid); track room.guid) {
              <label class="resource-default-option room">
                <input
                  type="checkbox"
                  [checked]="isResourceDefaultRoomSelected(room.guid)"
                  (change)="
                    onToggleResourceDefaultRoom(
                      room.guid,
                      $any($event.target).checked
                    )
                  "
                />
                {{ room.room_name }}
              </label>
              }
            </div>
            }
          </div>
        </div>
        }
      </section>
    </mat-expansion-panel>
  </mat-accordion>
  <div class="form-actions">
    <button
      mat-raised-button
      color="primary"
      [disabled]="saving"
      (click)="save()"
    >
      {{ saving ? "Gemmer..." : "Gem indstillinger" }}
    </button>
  </div>
  } @if (orgIdResolved && !loading && effectiveOrgId == null && !errorMessage) {
  <p class="no-org-message">
    Log ind med en bruger tilknyttet en organisation for at se og redigere
    indstillinger.
  </p>
  }
</div>
